<template>
    <div v-if="editor" class="d-flex flex-column">
        <div class="py-1">
            <b-button
                size="sm"
                :disabled="!allowedToEdit"
                :pressed.sync="editor.isActive('heading', { level: 1 })"
                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                :variant="variant"
            >
                <i class="fas fa-heading"></i><sub style="font-size: x-small">1</sub>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                size="sm"
                :pressed.sync="editor.isActive('heading', { level: 2 })"
            >
                <i class="fas fa-heading"></i><sub style="font-size: x-small">2</sub>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                :pressed.sync="editor.isActive('heading', { level: 3 })"
                size="sm"
            >
                <i class="fas fa-heading"></i><sub style="font-size: x-small">3</sub>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
                :pressed.sync="editor.isActive('heading', { level: 4 })"
                size="sm"
            >
                <i class="fas fa-heading"></i><sub style="font-size: x-small">4</sub>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
                :pressed.sync="editor.isActive('heading', { level: 5 })"
                size="sm"
            >
                <i class="fas fa-heading"></i><sub style="font-size: x-small">5</sub>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().toggleBold().run()"
                size="sm"
                :pressed.sync="editor.isActive('bold')"
            >
                <i class="fas fa-bold"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleItalic().run()"
                :pressed.sync="editor.isActive('italic')"
            >
                <i class="fas fa-italic"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleUnderline().run()"
                :pressed.sync="editor.isActive('underline')"
            >
                <i class="fas fa-underline"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleStrike().run()"
                :pressed.sync="editor.isActive('strike')"
            >
                <i class="fas fa-strikethrough"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleBulletList().run()"
                :pressed.sync="editor.isActive('bulletList')"
            >
                <i class="fas fa-list-ul"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleOrderedList().run()"
                :pressed.sync="editor.isActive('orderedList')"
            >
                <i class="fas fa-list-ol"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleSuperscript().run()"
                :pressed.sync="editor.isActive('superscript')"
            >
                <i class="fas fa-superscript"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleSubscript().run()"
                :pressed.sync="editor.isActive('subscript')"
            >
                <i class="fas fa-subscript"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                size="sm"
                @click="editor.chain().focus().toggleCode().run()"
                :pressed.sync="editor.isActive('code')"
            >
                <i class="fas fa-code"></i>
            </b-button>
            <b-button
                :variant="variant"
                :disabled="!allowedToEdit"
                @click="editor.chain().focus().setHorizontalRule().run()"
                size="sm"
            >
                <b>-</b>
            </b-button>
        </div>
        <editor-content class="rich-text-editor flex-grow-1"
                        :class="{'bg-color-private-message': isPrivate, 'private-message': isPrivate}"
                        :editor="editor"></editor-content>
    </div>
</template>
<script lang="js">
// original https://gitlab.com/c3711/dev-cs1/o253/oraweb/-/blob/524e3e9bae541f1adc00b4e63ceff95fbfccd404/resources/ts/components/form/CustomRichTextEditor.vue

// voir example https://github.com/kido1611/tiptap-vuejs-example/tree/main
import {Editor, EditorContent} from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Code from '@tiptap/extension-code'
import Link from '@tiptap/extension-link'
import HorizontalRule from '@tiptap/extension-horizontal-rule'

export default {
    components: {
        EditorContent,
    },

    props: {
        value: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: true,
        },
        isPrivate: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            editor: null,
            allowedToEdit: true,
            variant: 'outline-primary',
        }
    },

    watch: {
        value(value) {
            const isSame = this.editor.getHTML() === value

            if (isSame) {
                return
            }

            this.editor.commands.setContent(value, false)
        },
    },

    mounted() {
        this.editor = new Editor({
            content: this.value,
            editable: true,
            extensions: [
                StarterKit,
                Underline,
                Subscript,
                Superscript,
                Link,
            ],
            editorProps: {
                attributes: {
                    class: 'h-100',
                }
            },
            onUpdate: () => {
                this.$emit('input', this.editor.getHTML())
            },
        })
    },

    beforeDestroy() {
        this.editor.destroy()
    },
}
</script>
<style scoped>
.private-message {
    border-radius: 10px;
}
</style>
